import {
    useInfiniteQuery,
    useQuery,
} from '@tanstack/react-query';

import * as HostedZonesService from '../services/HostedZonesService';
import {HostedZone} from '../types';

// FNP-973 — READ-ONLY module: query hooks only. No mutation hooks, because the
// deployed schema exposes no hosted-zone mutation (writes are FNP-977). Nothing
// here invalidates, because nothing here writes.
export const hostedZonesKeys = {
    all: ['hosted-zones'] as const,
    lists: () => [...hostedZonesKeys.all, 'list'] as const,
    list: (parentDomainName?: string) => [...hostedZonesKeys.lists(), {parentDomainName}] as const,
    details: () => [...hostedZonesKeys.all, 'detail'] as const,
    detail: (id: string) => [...hostedZonesKeys.details(), id] as const,
};

// Page size is a SERVER concern under token pagination — fixed by the module,
// never a "rows per page" dropdown (§9 of the module checklist).
export const PAGE_SIZE = 25;

export const useHostedZonesInfiniteQuery = (parentDomainName?: string) => {
    return useInfiniteQuery({
        // parentDomainName is part of the key: omit a filter parameter and
        // changing it silently serves the previous filter's cache.
        queryKey: hostedZonesKeys.list(parentDomainName),
        queryFn: ({pageParam}: { pageParam: string | null }) =>
            HostedZonesService.fetchRecords(parentDomainName, PAGE_SIZE, pageParam),
        // MUST return undefined — returning null or the same token makes React
        // Query refetch forever.
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) {
                return undefined;
            }
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null,
        staleTime: 60_000,
    });
};

// The detail query is a SEPARATE fetch on purpose: `nameServers` is null on
// every list row and populated only by getHostedZone (see queries.ts). Reusing
// a cached list row here would render a zone with no delegation set.
export const useHostedZoneQuery = (id?: string, parentDomainName?: string) => {
    return useQuery({
        queryKey: hostedZonesKeys.detail(id ?? ''),
        queryFn: async (): Promise<HostedZone | undefined> => {
            if (typeof id === 'undefined') {
                throw new Error('id is required parameter');
            }
            return await HostedZonesService.fetch(id, parentDomainName);
        },
        enabled: typeof id !== 'undefined' && id !== '',
        staleTime: 60_000,
    });
};
